import axios, { AxiosInstance, AxiosResponse } from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import { getBaseUrl, getEnvironment } from './env';
import { loadingBus } from './loadingBus';
import { connectionBus } from './connectionBus';

const baseURL = getBaseUrl();

const instance: AxiosInstance = axios.create({
  baseURL,
  // Do not set default Content-Type here to allow FormData
});

instance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Ensure headers object exists
    config.headers = (config.headers || {}) as any;

    const skipLoading = String((config.headers as any)['X-Skip-Loading'] || '').toLowerCase() === 'true';
    (config as any).__skipLoading = skipLoading;
    if (!skipLoading) {
      loadingBus.increment();
    }

    // Attach Authorization if JWT is present
    try {
      const skipAuth = String((config.headers as any)['X-Skip-Auth'] || '').toLowerCase() === 'true';
      if (!skipAuth) {
        const token = localStorage.getItem('wg_tech_access_token');
        if (token) {
          (config.headers as any)['Authorization'] = `Bearer ${token}`;
        }
      } else {
        // Ensure Authorization is not sent when skipping auth
        try { delete (config.headers as any)['Authorization']; } catch {}
      }
    } catch {}

    // If data is plain object, set JSON Content-Type
    const isFormData = typeof FormData !== 'undefined' && (config as any).data instanceof FormData;
    if (!isFormData) {
      (config.headers as any)['Content-Type'] = 'application/json';
    }

    const env = getEnvironment();
    if (env) {
      (config.headers as any)['X-Environment'] = env;
    }

    return config;
  },
  (error) => {
    try {
      const skipLoading =
        Boolean((error as any)?.config?.__skipLoading) ||
        String(((error as any)?.config?.headers as any)?.['X-Skip-Loading'] || '').toLowerCase() === 'true';
      if (!skipLoading) {
        loadingBus.decrement();
      }
    } catch {}
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response: AxiosResponse) => {
    try {
      const skipLoading =
        Boolean((response as any)?.config?.__skipLoading) ||
        String(((response as any)?.config?.headers as any)?.['X-Skip-Loading'] || '').toLowerCase() === 'true';
      if (!skipLoading) {
        loadingBus.decrement();
      }
    } catch {}
    try {
      // Clear connection-refused state on any successful API response
      connectionBus.setRefused(false);
    } catch {}
    return response;
  },
  (error) => {
    try {
      const skipLoading =
        Boolean((error as any)?.config?.__skipLoading) ||
        String(((error as any)?.config?.headers as any)?.['X-Skip-Loading'] || '').toLowerCase() === 'true';
      if (!skipLoading) {
        loadingBus.decrement();
      }
    } catch {}
    try {
      const status: number | undefined = error?.response?.status;
      const skipAuth = String((error?.config?.headers as any)?.['X-Skip-Auth'] || '').toLowerCase() === 'true';
      if (!skipAuth && (status === 401 || status === 403)) {
        try { localStorage.removeItem('wg_tech_auth_user'); } catch {}
        try { localStorage.removeItem('wg_tech_auth_timestamp'); } catch {}
        try { localStorage.removeItem('wg_tech_access_token'); } catch {}

        const path = (typeof window !== 'undefined' && window.location) ? window.location.pathname : '';
        const isPublicPlanSelectionRoute = path.startsWith('/plan-selection') || path.startsWith('/selecao-plano') || path.startsWith('/planos') || path.startsWith('/plans');
        if (path && !path.startsWith('/login') && !isPublicPlanSelectionRoute) {
          try { window.location.href = '/login'; } catch {}
        }
      }
    } catch {}
    try {
      const code: string | undefined = (error && error.code) || undefined;
      const msg: string = String((error && error.message) || '');
      let errString = '';
      try { errString = String(error); } catch {}

      const isConnRefused = (
        (code === 'ERR_NETWORK') ||
        msg.toLowerCase().includes('network error') ||
        errString.includes('ERR_CONNECTION_REFUSED') ||
        msg.includes('ERR_CONNECTION_REFUSED')
      );

      if (isConnRefused) {
        connectionBus.setRefused(true);
      }
    } catch {}
    return Promise.reject(error);
  }
);

export default instance;
